import React, { useMemo, useEffect } from "react";
import { useLocation, useParams } from "react-router-dom";
import { startCase } from 'lodash';
import privacyPolicyContent from "../../components/templates/privacyPolicyTemplate";

const PrivacyPolicy = () => {
  const location = useLocation();

  // Function to format slug to company name using updated regex
  const formatSlugToName = (slug) => {
    if (!slug || slug.toLowerCase() === 'default') {
      return "Default Company";
    }

    let processedSlug = slug.trim();

    // List of common company suffixes
    const suffixes = ['LLC', 'Inc', 'Corp', 'Co', 'Ltd', 'LLP', 'PLC', 'GmbH', 'Pty', 'S.A.', 'S.L.'];

    // Handle suffixes
    let detectedSuffix = '';
    for (let suffix of suffixes) {
      const regex = new RegExp(`${suffix}$`, 'i'); // Case-insensitive match at the end
      if (regex.test(processedSlug)) {
        detectedSuffix = suffix;
        // Remove the suffix from the slug
        processedSlug = processedSlug.slice(0, -suffix.length);
        break;
      }
    }

    // Replace underscores and hyphens with spaces
    processedSlug = processedSlug.replace(/[_-]/g, ' ');

    // Insert spaces before uppercase letters ONLY IF the next character is not a number
    processedSlug = processedSlug.replace(/([a-zA-Z])([A-Z])(?!\d)/g, '$1 $2');

    // Use startCase to convert to Start Case (each word capitalized)
    processedSlug = startCase(processedSlug);

    // Remove spaces between letters and numbers (e.g., G 4 -> G4)
    processedSlug = processedSlug.replace(/([A-Za-z])\s+(\d)/g, '$1$2');

    // Append the suffix if detected
    if (detectedSuffix) {
      return `${processedSlug} ${detectedSuffix}`;
    }

    return processedSlug;
  };
  
  // Function to extract and format companySlug from query parameters
  const getCompanyName = () => {
    const params = new URLSearchParams(location.search);
    const companyFromQuery = params.get("appFlavor");
    console.log("appFlavor:", companyFromQuery); // Debugging
    return formatSlugToName(companyFromQuery);
  };

  const companyName = getCompanyName();

  // Function to sanitize the company name to prevent XSS
  const sanitizeCompanyName = (name) => {
    const div = document.createElement("div");
    div.textContent = name;
    return div.innerHTML;
  };

  const sanitizedCompanyName = sanitizeCompanyName(companyName);

  // Function to replace "Tracy Luxury LLC" with the desired company name
  const replaceCompanyName = (text, name) => {
    // Handle variations like "Tracy Luxury LLC", "Tracy Luxury LLC.", "Tracy Luxury LLC's"
    const regex = /\bTracy Luxury LLC('s)?\.?\b/g;
    return text.replace(regex, (match, p1) => {
      // Append 's if present
      return p1 ? `${name}'s` : name;
    });
  };

  // Memoize the processed content for performance optimization
  const processedContent = useMemo(() => {
    return replaceCompanyName(privacyPolicyContent, sanitizedCompanyName);
  }, [privacyPolicyContent, sanitizedCompanyName]);

  // Update the document title and meta description for SEO
  useEffect(() => {
    document.title = `${sanitizedCompanyName} Privacy Policy`;
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', `${sanitizedCompanyName} Privacy Policy`);
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = `${sanitizedCompanyName} Privacy Policy`;
      document.head.appendChild(meta);
    }
  }, [sanitizedCompanyName]);

  // Debugging: Check if processedContent is being generated
  console.log("Processed Content:", processedContent);

  return (
    <div style={{ padding: "20px", maxWidth: "800px", margin: "0 auto" }}>
      {processedContent ? (
        <div dangerouslySetInnerHTML={{ __html: processedContent }} />
      ) : (
        <p>Loading Privacy Policy...</p> // Fallback content
      )}
    </div>
  );
};

export default PrivacyPolicy;